export type ProductionPdfBoxes = Record<'media' | 'crop' | 'bleed' | 'trim' | 'art', [number, number, number, number]>;
export interface ProductionSheetProfile {
  id: string;
  version: number;
  sourceSha256: string;
  pages: Array<{ id: string; label: string; labels?: Partial<Record<'nl' | 'en' | 'de' | 'fr', string>>; widthMm: number; heightMm: number; guideUrl: string; pdfBoxes?: ProductionPdfBoxes }>;
}
export function normalizeProductionSheetProfile(value: unknown): ProductionSheetProfile | null {
  if (value == null) return null;
  const p = value as ProductionSheetProfile;
  if (!p || typeof p !== 'object' || !/^[\w-]{1,80}$/.test(p.id) || !Number.isInteger(p.version) || p.version < 1 || !/^[a-f0-9]{64}$/i.test(p.sourceSha256) || !Array.isArray(p.pages) || p.pages.length < 1 || p.pages.length > 8) throw new Error('Invalid production sheet profile');
  const ids = new Set<string>();
  const pages = p.pages.map(page => {
    if (!page || typeof page.id !== 'string' || !/^[\w-]{1,80}$/.test(page.id) || ids.has(page.id) || typeof page.label !== 'string' || !page.label.trim() || page.label.length > 200) throw new Error('Invalid production sheet page');
    ids.add(page.id);
    if (![page.widthMm, page.heightMm].every(v => typeof v === 'number' && Number.isFinite(v) && v > 0 && v <= 5000)) throw new Error('Invalid production sheet geometry');
    const url = page.guideUrl;
    if (typeof url !== 'string' || /[\\\s%?#]/.test(url)) throw new Error('Invalid guide URL');
    if (url.startsWith('/')) {
      if (!url.startsWith('/templates/products/') || url.split('/').some(v => v === '..' || v === '.') || !/\.png$/i.test(url)) throw new Error('Invalid guide URL');
    } else {
      const parsed = new URL(url);
      if (parsed.protocol !== 'https:' || !parsed.hostname.endsWith('.public.blob.vercel-storage.com') || parsed.port || parsed.username || parsed.password || !/\.png$/i.test(parsed.pathname)) throw new Error('Invalid guide URL');
    }
    const labels: NonNullable<typeof page.labels> = {};
    if (page.labels !== undefined) {
      if (!page.labels || typeof page.labels !== 'object' || Array.isArray(page.labels)) throw new Error('Invalid page labels');
      for (const [language, label] of Object.entries(page.labels)) {
        if (!['nl', 'en', 'de', 'fr'].includes(language) || typeof label !== 'string' || !label.trim() || label.length > 200) throw new Error('Invalid page labels');
        labels[language as keyof typeof labels] = label;
      }
    }
    let pdfBoxes: ProductionPdfBoxes | undefined;
    if (page.pdfBoxes !== undefined) {
      const boxes = page.pdfBoxes;
      for (const key of ['media', 'crop', 'bleed', 'trim', 'art'] as const) {
        const box = boxes?.[key];
        if (!Array.isArray(box) || box.length !== 4 || !box.every(Number.isFinite) || box[2] <= box[0] || box[3] <= box[1]) throw new Error('Invalid PDF page boxes');
      }
      if (boxes.media[0] !== 0 || boxes.media[1] !== 0 || Math.abs(boxes.media[2] * 25.4 / 72 - page.widthMm) > 0.01 || Math.abs(boxes.media[3] * 25.4 / 72 - page.heightMm) > 0.01) throw new Error('PDF boxes do not match sheet geometry');
      for (const key of ['crop', 'bleed', 'trim', 'art'] as const) if (boxes[key][0] < 0 || boxes[key][1] < 0 || boxes[key][2] > boxes.media[2] || boxes[key][3] > boxes.media[3]) throw new Error('PDF page box outside media');
      pdfBoxes = Object.fromEntries(['media', 'crop', 'bleed', 'trim', 'art'].map(key => [key, [...boxes[key as keyof ProductionPdfBoxes]]])) as ProductionPdfBoxes;
    }
    return { id: page.id, label: page.label, labels, widthMm: page.widthMm, heightMm: page.heightMm, guideUrl: url, ...(pdfBoxes ? { pdfBoxes } : {}) };
  });
  return { id: p.id, version: p.version, sourceSha256: p.sourceSha256.toLowerCase(), pages };
}
export function matchesProductionSheetProfile(current: ProductionSheetProfile, saved: unknown): boolean {
  try { return JSON.stringify(normalizeProductionSheetProfile(current)) === JSON.stringify(normalizeProductionSheetProfile(saved)); } catch { return false; }
}
export function productionSheetDraftScope(profile: ProductionSheetProfile, scope?: string) {
  return `${scope || 'product'}:production:${profile.id}:v${profile.version}:${profile.sourceSha256}`;
}
export function isProductionSheetSnapshotCurrent(profile: ProductionSheetProfile, pageId: string, snapshot: any): boolean {
  return snapshot?.printenMeta?.productionSheetPageId === pageId && matchesProductionSheetProfile(profile, snapshot?.printenMeta?.productionSheetProfile);
}
export function requireProductionSheetPages(profile: ProductionSheetProfile, designs: Record<number, any>) {
  return profile.pages.map((page, index) => {
    const snapshot = designs[index + 1];
    const json = snapshot?.json || snapshot;
    if (!json || !Array.isArray(json.objects) || !json.objects.some((object: any) => object && object.visible !== false && !object.excludeFromExport && !object.guideType && !object.templateType)) throw new Error(`Missing artwork: ${page.label}`);
    return { page, snapshot, json };
  });
}
export function stripProductionSheetGuides(json: any): any {
  const printable = (object: any): boolean => Boolean(object && !object.excludeFromExport && !object.guideType && !object.templateType);
  return { ...json, objects: (Array.isArray(json?.objects) ? json.objects : []).filter(printable).map((object: any) => Array.isArray(object.objects) ? { ...object, objects: stripProductionSheetGuides(object).objects } : { ...object }) };
}
